import React, { useContext, useEffect } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'
import ChartLine from '../../../../components/charts/ChartLine'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { formatCurrency } from '../../../../utils/Helpers'
import { PharmaciesDetailsFiltersContext } from '../../../../context/PharmaciesDetailsFiltersContext'
import { ORDER_STATUS, AGG_STATUS_DELIVERED, AGG_STATUS_CANCELLED } from '../../../../types/Order'

const GET_DATA = gql`
  query DailyOrderTotals ($filters: JSON) {
    ordersAggs(
      size: 0,
      filters: $filters,
      query: {
        bool: {
          filter: [
            {
              terms: {
                new_status: [
                  "${ORDER_STATUS.DELIVERED}",
                  "${ORDER_STATUS.CANCELLED}"
                ]
              }
            }
          ]
        }
      },
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  new_status: "${ORDER_STATUS.DELIVERED}"
                }
              },
              aggs: {
                total: {
                  sum: {
                    field: "total"
                  }
                }
              }
            },
            ${AGG_STATUS_CANCELLED}: {
              filter: {
                term: {
                  new_status: "${ORDER_STATUS.CANCELLED}"
                }
              },
              aggs: {
                total: {
                  sum: {
                    field: "total"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

type Series = {
  name: string
  data: number[]
}

type BucketTypes = {
  key_as_string: string,
  key: number,
  doc_count: number,
  cancelled: {
    doc_count : number,
    total : {
      value : number
    }
  },
  delivered: {
    doc_count : number,
    total : {
      value : number
    }
  }
}

const DailyOrderTotals = (): JSX.Element => {
  const { filters } = useContext(PharmaciesDetailsFiltersContext)
  const categories: Array<string> = []
  const series: Array<Series> = []
  series.push({ name: `${ORDER_STATUS.CANCELLED}`, data: [] })
  series.push({ name: `${ORDER_STATUS.DELIVERED}`, data: [] })

  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getData({
      variables: { filters }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data) return <ChartLineSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.ordersAggs.aggregations) return <p>No data found</p>

  const { buckets } = data.ordersAggs.aggregations.salesOverTime

  series.map(serie => {
    buckets.map((bucket: BucketTypes) => {
      if (serie.name === ORDER_STATUS.DELIVERED) {
        serie.data.push(bucket[AGG_STATUS_DELIVERED].total.value)
      } else if (serie.name === ORDER_STATUS.CANCELLED) {
        serie.data.push(bucket[AGG_STATUS_CANCELLED].total.value)
      } else {
        serie.data.push(0)
      }
    })
  })

  buckets.map((bucket: BucketTypes) => {
    categories.push(moment(bucket.key_as_string).format('MM-DD-YYYY'))
  })

  const options = {
    chart: {
      id: 'daily-orders-totals-per-day'
    },
    xaxis: {
      categories,
      type: 'date',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories,
      labels: {
        formatter: (value: number) => {
          return formatCurrency(value, 2)
        }
      }
    },
    tooltip: {
      y: {
        formatter: (value: number) => {
          return formatCurrency(value, 2)
        }
      }
    }
  }

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>Total de pedidos (por dia) </Typography>
    <ChartLine options={options} series={series} theme={ChartLineThemes.twocolors} height={320} type="area"/>
  </Paper>
}

export default DailyOrderTotals
